/**
 * Contract cash waterfall — committed vs. at-risk cash by season (v2 handoff).
 *
 * Real data only. Every figure is arithmetic on recorded contract rows:
 *  - guaranteed  = the season's recorded guaranteed_salary
 *  - atRisk      = the season's recorded cash (base + bonus) minus its
 *                  recorded guarantee — money the athlete must still earn
 *  - option      = cash falling in the contract's final `option_years`
 *                  seasons (an option year's cash is at-risk until the
 *                  option is exercised)
 *  - fee         = commission_rate × season cash, only for contracts that
 *                  carry a rate; contracts without one contribute nothing
 *
 * A contract with no per-season rows contributes nothing and is counted in
 * `uncovered` so the panel can state its coverage instead of implying the
 * whole book is on screen. Nothing here spreads a total across years.
 */

import type { Contract } from '../types'

export type WaterfallSeason = {
  year: number
  guaranteed: number
  atRisk: number
  option: number
  fee: number
}

export type CashWaterfall = {
  seasons: WaterfallSeason[]
  /** contracts whose rows produced at least one season figure */
  covered: number
  /** playing contracts with no usable per-season rows */
  uncovered: number
  /** of the covered contracts, how many carry a commission rate (fee line coverage) */
  feeCovered: number
}

type YearRow = {
  year?: number | string | null
  base_salary?: number | null
  prorated_bonus?: number | null
  per_game_roster_bonus?: number | null
  workout_bonus?: number | null
  cash_payout?: number | null
  guaranteed_salary?: number | null
}

function num(v: unknown): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** A season's total cash: the recorded payout if present, else the sum of its recorded parts. */
function rowCash(r: YearRow): number {
  if (r.cash_payout != null && Number.isFinite(Number(r.cash_payout))) return num(r.cash_payout)
  return num(r.base_salary) + num(r.prorated_bonus) + num(r.per_game_roster_bonus) + num(r.workout_bonus)
}

export function computeCashWaterfall(contracts: Contract[]): CashWaterfall {
  const byYear = new Map<number, WaterfallSeason>()
  let covered = 0
  let uncovered = 0
  let feeCovered = 0

  for (const c of contracts) {
    const rows = (Array.isArray((c as any).yearly_breakdown) ? (c as any).yearly_breakdown : []) as YearRow[]
    const usable = rows.filter(r => {
      const y = Number(r.year)
      return Number.isFinite(y) && y > 1900 && (rowCash(r) > 0 || num(r.guaranteed_salary) > 0)
    })
    if (usable.length === 0) {
      uncovered++
      continue
    }
    covered++

    const rate = num((c as any).commission_rate)
    if (rate > 0) feeCovered++

    // Option years are the final N seasons of the recorded term.
    const optionYears = new Set<number>()
    const optN = Math.min(num((c as any).option_years), usable.length)
    if (optN > 0) {
      const years = usable.map(r => Number(r.year)).sort((a, b) => a - b)
      for (const y of years.slice(-optN)) optionYears.add(y)
    }

    for (const r of usable) {
      const year = Number(r.year)
      const cash = rowCash(r)
      const gtd = Math.min(num(r.guaranteed_salary), cash > 0 ? cash : num(r.guaranteed_salary))
      const season = byYear.get(year) ?? { year, guaranteed: 0, atRisk: 0, option: 0, fee: 0 }

      if (optionYears.has(year)) {
        // The whole season rides on the option being exercised.
        season.option += Math.max(cash, gtd)
      } else {
        season.guaranteed += gtd
        season.atRisk += Math.max(0, cash - gtd)
      }
      if (rate > 0) season.fee += (rate / 100) * Math.max(cash, gtd)
      byYear.set(year, season)
    }
  }

  return {
    seasons: [...byYear.values()].sort((a, b) => a.year - b.year),
    covered,
    uncovered,
    feeCovered,
  }
}
